import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { Row, Col, CardBody, Card, Button } from "reactstrap";
import L, { LeafletMouseEvent } from "leaflet";

import { SessionStorageWrapper } from "models/session-storage-wapper";
import { selectedLocationKey } from "models/session-storage-keys";
import LeafletMapWrapper from "components/LeafletMapWrapper";
import { LeafletDrawingManager } from "components/LeafletDrawingManager";
import { Address } from "models/address";
import EstimateSidebar from "./components/EstimateSidebar";
import { FenceSegment, LatLon, removeGateFromSegments } from "models/fenceSegment";
import { FenceOutline } from "./components/FenceOutline";
import SelectFenceStyleModal from "./components/SelectStyleModal";
import { FenceSegmentAttribute } from "models/fenceAttribute";
import { ensure } from "utils";
import { FenceGate, GateSizeEnum } from "models/fenceGate";
import { GateMarkers } from "./components/GateMarker";
import { EstimateTotal } from "./components/EstimateTotal";
import { ContactInformation } from "models/contact";
import { EstimateAction, EstimateApi } from "api/api";
import { EstimateTabBar } from "./components/EstimateTabBar";

import "react-toastify/dist/ReactToastify.css";
import { HelpText } from "./components/HelpText";

export enum EstimatePageCurrentMode {
  None = "None",
  DrawingFence = "Draw",
  EditStyle = "Edit",
  AddSingleGate = "AddSingleGate",
  AddDoubleGate = "AddDoubleGate",
}

export default function EstimatePage() {
  const navigate = useNavigate();
  const { portalId, estimateId } = useParams();

  const mapRef = React.useRef<L.Map>();

  const [fenceSegments, setFenceSegments] = React.useState<Array<FenceSegment>>([]);
  const [selectedFenceId, setSelectedFenceId] = React.useState("");
  const [currentPageMode, setPageMode] = React.useState<EstimatePageCurrentMode>(EstimatePageCurrentMode.None);
  const [selectedAddress, setSelectedAddress] = React.useState<Address>();
  const [contactInfo, setContactInfo] = React.useState<ContactInformation>();
  const [centerCoords, setCenterCoords] = React.useState<{ lat: number; lon: number }>();
  const [activeTab, setActiveTab] = React.useState<"details" | "map">("details");

  const isMobileView = () => window.innerWidth < 576;

  React.useEffect(() => {
    if (!SessionStorageWrapper.keyExists(selectedLocationKey)) {
      navigate(`/${portalId}/designer`);
    } else {
      const selectedAddress = SessionStorageWrapper.getItem<Address>(selectedLocationKey);
      if (selectedAddress === undefined) navigate("/");
      setCenterCoords({ lat: selectedAddress!.lat, lon: selectedAddress!.lon });
      setSelectedAddress(selectedAddress);
    }
  }, [setCenterCoords, setSelectedAddress, navigate, portalId]);

  const handlePolylineComplete = (coords: LatLon[]) => {
    const newFenceSegment = new FenceSegment();
    coords.forEach((c) => newFenceSegment.addCoordinates(c.lat, c.lon));
    fenceSegments.push(newFenceSegment);
    setFenceSegments([...fenceSegments]);
    setPageMode(EstimatePageCurrentMode.None);
  };

  const handleAddFenceBtnClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setPageMode(EstimatePageCurrentMode.DrawingFence);
    if (isMobileView()) setActiveTab("map");
  };

  const handleEditStyleBtnClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setPageMode(EstimatePageCurrentMode.EditStyle);
    setSelectedFenceId(e.currentTarget.value);
  };

  const handleDeleteStyleBtnClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const filteredSegments = fenceSegments.filter((x) => x.id !== e.currentTarget.value);
    EstimateApi.saveEstimate(estimateId!, filteredSegments, EstimateAction.RemoveSegment).then(() => {
      setFenceSegments(filteredSegments);
    });
  };

  const handleCancelDrawingBtnClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setPageMode(EstimatePageCurrentMode.None);
  };

  const handleAddGateDropDownSelect =
    (fenceSegmentId: string, size: GateSizeEnum) => (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
      setSelectedFenceId(fenceSegmentId);
      if (size === GateSizeEnum.SINGLE) {
        setPageMode(EstimatePageCurrentMode.AddSingleGate);
      } else if (size === GateSizeEnum.DOUBLE) {
        setPageMode(EstimatePageCurrentMode.AddDoubleGate);
      }
      if (isMobileView()) setActiveTab("map");
    };

  const hadleDeleteGateBtnClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    removeGateFromSegments(fenceSegments, e.currentTarget.value);
    EstimateApi.saveEstimate(estimateId!, fenceSegments, EstimateAction.RemoveGate).then(() => {
      setFenceSegments([...fenceSegments]);
    });
  };

  const handleOnFenceOutlineClick = (fenceId: string) => (e: LeafletMouseEvent) => {
    if (selectedFenceId !== fenceId) return;
    const selectedFence = ensure(fenceSegments.find((x) => x.id === fenceId));
    const newGate = new FenceGate();
    newGate.coords = { lat: e.latlng.lat, lon: e.latlng.lng };

    if (currentPageMode === EstimatePageCurrentMode.AddSingleGate) {
      newGate.size = GateSizeEnum.SINGLE;
      selectedFence.gates.push(newGate);
      setFenceSegments([...fenceSegments]);
      EstimateApi.saveEstimate(estimateId!, fenceSegments, EstimateAction.AddGate);
    } else if (currentPageMode === EstimatePageCurrentMode.AddDoubleGate) {
      newGate.size = GateSizeEnum.DOUBLE;
      selectedFence.gates.push(newGate);
      setFenceSegments([...fenceSegments]);
      EstimateApi.saveEstimate(estimateId!, fenceSegments, EstimateAction.AddGate);
    }
    setSelectedFenceId(fenceId);
    setPageMode(EstimatePageCurrentMode.None);
  };

  const handleFenceSegmentAttributeSelect = (attributes: FenceSegmentAttribute[]) => {
    const indx = ensure(fenceSegments.findIndex((x) => x.id === selectedFenceId));
    attributes.forEach((atr) => {
      atr.setOnSegment(fenceSegments[indx]);
    });
    EstimateApi.saveEstimate(estimateId!, fenceSegments, EstimateAction.AddSegment).then(() => {
      setFenceSegments([...fenceSegments]);
      setPageMode(EstimatePageCurrentMode.None);
    });
  };

  const handleContactInfoProvided = (c: ContactInformation) => {
    EstimateApi.saveContactInformation(estimateId!, c)
      .then(() => setContactInfo(c))
      .catch(() => toast.error("Error saving contact info."));
  };

  const handleGetFullQuote = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    EstimateApi.saveEstimate(estimateId!, fenceSegments, EstimateAction.ReguestQuote).then(() => {
      SessionStorageWrapper.removeItem(selectedLocationKey);
      navigate(`/${portalId!}/thank-you/${estimateId}`);
    });
  };

  const IsMapDrawingMode = (): boolean =>
    currentPageMode === EstimatePageCurrentMode.AddDoubleGate ||
    currentPageMode === EstimatePageCurrentMode.AddSingleGate ||
    currentPageMode === EstimatePageCurrentMode.DrawingFence;

  const showSidebar = !isMobileView() || activeTab === "details";
  const showMap = !isMobileView() || activeTab === "map";

  return (
    <div className="flex-grow-1 d-flex flex-column overflow-hidden">
      <EstimateTabBar activeTab={activeTab} onTabChange={setActiveTab} />

      <Row xs={1} sm={2} className="gx-0 flex-grow-1">
        {showSidebar && (
          <Col className="bg-white border-end border-2 border-primary px-3" md={4}>
            <Row className="pt-3 pb-2">
              <Col className="d-flex justify-content-center">
                <div className="ds-label">Estimate Details</div>
              </Col>
            </Row>
            <Row className="pb-2">
              <Col>
                <Card className="ds-card-accent shadow-sm mb-2">
                  <CardBody className="py-2">
                    <div className="ds-label mb-1">Worksite</div>
                    <address className="mb-0 small text-muted">
                      {selectedAddress?.street}
                      <br />
                      {selectedAddress?.city}, {selectedAddress?.state}, {selectedAddress?.zipCode}
                    </address>
                  </CardBody>
                </Card>
              </Col>
            </Row>
            <Row>
              <Col>
                <EstimateSidebar
                  onEditStyleBtnClick={handleEditStyleBtnClick}
                  onDeleteBtnClick={handleDeleteStyleBtnClick}
                  onCancelDrawingInProgress={handleCancelDrawingBtnClick}
                  onAddGateDropDownSelect={handleAddGateDropDownSelect}
                  onGateDeleteBtnClick={hadleDeleteGateBtnClick}
                  selectedFenceSegmentId={selectedFenceId}
                  fenceSegments={fenceSegments}
                  currentMode={currentPageMode}
                />
              </Col>
            </Row>
            {!IsMapDrawingMode() && (
              <Row className="pt-2 pb-2">
                <Col className="d-flex align-items-center justify-content-center">
                  <Button color="primary" onClick={handleAddFenceBtnClick}>
                    + Add Fence
                  </Button>
                </Col>
              </Row>
            )}
            <Row>
              <Col>
                <EstimateTotal
                  fences={fenceSegments}
                  isContactInformationProvided={contactInfo !== undefined}
                  onContactInformationProvided={handleContactInfoProvided}
                  onGetFullQuoteClick={handleGetFullQuote}
                />
              </Col>
            </Row>
          </Col>
        )}

        {showMap && (
          <Col className="p-0" md={8}>
            <div style={{ position: "relative" }}>
              {centerCoords !== undefined && (
                <LeafletMapWrapper
                  lat={centerCoords.lat}
                  lon={centerCoords.lon}
                  mapRef={mapRef}
                  isDrawingModeEnabled={IsMapDrawingMode()}
                >
                  <FenceOutline
                    selectedFenceId={selectedFenceId}
                    fences={fenceSegments}
                    onClick={handleOnFenceOutlineClick}
                  />
                  <GateMarkers fences={fenceSegments} />
                  <LeafletDrawingManager
                    isDrawingModeEnabled={currentPageMode === EstimatePageCurrentMode.DrawingFence}
                    isMobile={isMobileView()}
                    onPolylineComplete={handlePolylineComplete}
                  />
                </LeafletMapWrapper>
              )}
              <HelpText isMobile={false} currentMode={currentPageMode} isInDrawingMode={IsMapDrawingMode()} />
            </div>
          </Col>
        )}
      </Row>

      {currentPageMode === EstimatePageCurrentMode.EditStyle && (
        <SelectFenceStyleModal
          onCancelClick={() => setPageMode(EstimatePageCurrentMode.None)}
          setSelectedAttributes={handleFenceSegmentAttributeSelect}
          selectedFenceSegment={ensure(fenceSegments.find((x) => x.id === selectedFenceId))}
        />
      )}
      <ToastContainer />
    </div>
  );
}
